<template>
  <modal
    name="campanhaComercialEdit"
    height="auto"
    width="900"
    :scrollable="true"
    :clickToClose="false"
    @before-open="loadDependencias"
  >
    <CloseModal :close="close" />
    <Loading :isActive="loading" />
    <div class="modalbox">
      <div class="modalbox-content">
        <CAlert v-if="isErpLocked" show color="warning" class="mb-2">
          <strong>Campanha importada do ERP.</strong>
          Os campos de configuração estão bloqueados para edição.
        </CAlert>

        <CTabs variant="tabs" class="nav-tabs-boxed">
          <CTab title="Informações">
            <StepInformacoes
              :item="item"
              :validacao="validation"
              :isErpLocked="isErpLocked"
            />
          </CTab>
          <CTab title="Tipo de Campanha">
            <StepTipoCampanha
              :item="item"
              :validacao="validation"
              :isErpLocked="isErpLocked"
            />
          </CTab>
          <CTab title="Regras">
            <StepRegras
              :item="item"
              :dimensoes="dimensoes"
              :validacao="validation"
              :isErpLocked="isErpLocked"
            />
          </CTab>
          <CTab title="Brindes">
            <StepBrindes :item="item" />
          </CTab>
          <CTab title="Restrições">
            <StepRestricoes
              :item="item"
              :regioesRepresentantes="regioesRepresentantes"
            />
          </CTab>
          <CTab title="Resumo">
            <StepResumo :item="item" />
          </CTab>
        </CTabs>
      </div>
    </div>
    <div class="modal-footer-bar">
      <CButton variant="outline" color="secondary" @click="close">
        Cancelar
      </CButton>
      <CButton color="primary" @click="handleSubmit" :disabled="isErpLocked">
        Salvar Alterações
      </CButton>
    </div>
  </modal>
</template>

<script>
import SimpleVueValidation from "simple-vue-validator";
import moment from "moment";
import { get, post, put } from "@/helpers/apiRequest";
import { filtrarValidos } from "@/helpers/campanhas";
import { normalizeCampanhaParaApi } from "../helpers/campanhaDefaults";
import CloseModal from "@/components/CloseModal";
import Loading from "@/components/Loading.vue";
import StepInformacoes from "../sections/StepInformacoes.vue";
import StepTipoCampanha from "../sections/StepTipoCampanha.vue";
import StepRegras from "../sections/StepRegras.vue";
import StepBrindes from "../sections/StepBrindes.vue";
import StepRestricoes from "../sections/StepRestricoes.vue";
import StepResumo from "../sections/StepResumo.vue";
import _find from "lodash/find";

const Validator = SimpleVueValidation.Validator.create({
  templates: {
    required: "*obrigatório",
    greaterThan: "Valor deve ser maior que 0",
  },
});

export default {
  name: "CampanhaFormModal",
  props: ["item", "close"],
  components: {
    CloseModal,
    Loading,
    StepInformacoes,
    StepTipoCampanha,
    StepRegras,
    StepBrindes,
    StepRestricoes,
    StepResumo,
  },
  data() {
    return {
      loading: false,
      dimensoes: [],
      regioesRepresentantes: [],
    };
  },
  validators: {
    "item.descricao": (value) => Validator.value(value).required(),
    "item.funcionamento": (value) => Validator.value(value).required(),
    "item.dataInicio": (value) => Validator.value(value).required(),
    "item.dataFim": (value) => Validator.value(value).required(),
    "item.validacao": (value) => Validator.value(value).required(),
    "item.tipo": (value) => Validator.value(value).required(),
    "item.dimensoes, item.tipo": function (dimensoes, tipo) {
      if (tipo === "SEGMENTACAO" || tipo === "PERSONALIZADO") {
        return Validator.value(dimensoes).required();
      }
    },
    "item.mixProdutos, item.tipo": function (mixProdutos, tipo) {
      if (tipo === "MIX") {
        return Validator.value(mixProdutos).required();
      }
    },
  },
  computed: {
    isErpLocked() {
      return !!(this.item.erpId && this.item.id);
    },
    dimensoesValidas() {
      const validas = filtrarValidos(
        this.item.dimensoes,
        this.dimensoes,
        (dim, col) => {
          const coluna = col.coluna;
          const descricao = _find(col.dimensoes, (d) => d.id === dim.dimensao);
          return coluna === dim.coluna && !!descricao?.desc;
        }
      );

      if (validas.length !== this.item.dimensoes.length) {
        this.item.dimensoes = validas;
      }

      return validas;
    },
  },
  methods: {
    handleSubmit() {
      this.$validate().then((success) => {
        if (success) {
          const normalized = normalizeCampanhaParaApi(this.item);
          if (this.item.id) {
            this.update(normalized);
          } else {
            this.create(normalized);
          }
        }
      });
    },
    loadDimensoes() {
      get("/admin/campanhas-dimensoes")
        .then((json) => {
          this.dimensoes = json;
        })
        .catch(() => {});
    },
    loadRegioesRepresentantes() {
      get("/admin/regioes-representantes")
        .then((json) => {
          this.regioesRepresentantes = json;
        })
        .catch(() => {});
    },
    loadDependencias() {
      this.loadDimensoes();
      this.loadRegioesRepresentantes();
      this.validation.reset();
    },
    create(item) {
      this.loading = true;
      post("/admin/campanhas-comerciais", item)
        .then(() => {
          this.close();
        })
        .catch((err) => {
          this.$swal({
            title: "Falha ao cadastrar",
            text: err.message,
            icon: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    update(item) {
      this.loading = true;
      put(`/admin/campanhas-comerciais/${item.id}`, item)
        .then(() => {
          this.close();
        })
        .catch((err) => {
          this.loading = false;
          this.$swal({
            title: "Falha ao atualizar",
            text: err.message,
            icon: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.modal-footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}
</style>

<style>
/* Compactar formulário dentro do modal de campanhas */
.modalbox .tab-pane {
  padding: 14px 16px !important;
}

.modalbox .form-group {
  margin-bottom: 10px;
}

.modalbox .row {
  margin-left: -6px;
  margin-right: -6px;
}

.modalbox .row > [class*="col"] {
  padding-left: 6px;
  padding-right: 6px;
}
</style>
